import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import LinearLayout from "../../../shared/components/LinearLayout";
import Button from "../../../shared/components/Button";
import { Spinner } from "@/shared/components/ui/spinner";
import { isApiError } from "@/shared/api";
import {
  type IMembershipPlan,
  type MembershipBillingType,
} from "../interfaces/membership.interface";
import {
  useMembershipLevels,
  useMembershipStatus,
  useSetupMembership,
} from "../services";
import {
  BILLING_LABELS,
  mapMembershipLevelToPlan,
  sortMembershipLevels,
} from "../utils/membership";
import {
  clearMembershipSelection,
  getMembershipSelectionRaw,
} from "@/shared/utils/membership";
import { useProfile } from "@/features/profile/services";
import {
  resolveEduKycUrlAfterMembershipSetup,
  resolveRouteAfterSetupMembership,
  resolveRouteAfterSetupMembershipAlreadyCompleted,
  resolveRouteForActiveMembership,
} from "../lib/membership-flow";
import { isKycVerifiedStatus } from "@/features/kyc/utils/kyc-state";
import {
  buildPaymentSuccessCallbackUrl,
  openPaymentCheckoutLink,
  openPendingPaymentCheckoutWindow,
  redirectToPaymentCheckout,
} from "../lib/payment-callback";
import {
  PAYMENT_STATUS_REFETCH_INTERVAL_MS,
  resolveRouteAfterPendingPaymentCheck,
} from "../lib/pending-payment";

type StoredMembershipSelection = {
  id?: string;
  name?: string;
  billingType?: MembershipBillingType;
  billingPeriod?: string;
  price?: string | null;
  amount?: number | null;
  currency?: string | null;
  planCode?: string | null;
};

const parseStoredSelection = (
  rawValue: string | null
): StoredMembershipSelection | null => {
  if (!rawValue) {
    return null;
  }

  const normalizeBillingType = (
    value?: string | null
  ): MembershipBillingType | undefined => {
    if (!value) {
      return undefined;
    }

    const normalized = value.toUpperCase();
    if (normalized === "MONTHLY") {
      return "MONTHLY";
    }
    if (normalized === "ANNUAL" || normalized === "YEARLY") {
      return "ANNUAL";
    }
    return undefined;
  };

  try {
    const parsed = JSON.parse(rawValue) as Record<string, unknown>;
    return {
      id: typeof parsed.id === "string" ? parsed.id : undefined,
      name: typeof parsed.name === "string" ? parsed.name : undefined,
      billingType: normalizeBillingType(parsed.billingType as string | null),
      billingPeriod:
        typeof parsed.billingPeriod === "string"
          ? parsed.billingPeriod
          : undefined,
      price: typeof parsed.price === "string" ? parsed.price : undefined,
      amount:
        typeof parsed.amount === "number"
          ? (parsed.amount as number)
          : undefined,
      currency:
        typeof parsed.currency === "string" ? parsed.currency : undefined,
      planCode:
        typeof parsed.planCode === "string" ? parsed.planCode : undefined,
    };
  } catch {
    return {
      name: rawValue,
      billingType: normalizeBillingType(rawValue),
    };
  }
};

const findPlanMatch = (
  plans: IMembershipPlan[],
  selection: StoredMembershipSelection | null
): IMembershipPlan | null => {
  if (!selection) {
    return null;
  }

  const { id, name, billingType } = selection;
  const matchesBillingType = (plan: IMembershipPlan) =>
    !billingType ||
    !plan.billingType ||
    plan.billingType === billingType ||
    (billingType === "ANNUAL" &&
      plan.billingType === undefined &&
      plan.billingPeriod?.toLowerCase() === "yearly");

  if (id) {
    const matchById = plans.find(
      (plan) => plan.id === id && matchesBillingType(plan)
    );
    if (matchById) {
      return matchById;
    }
  }

  if (name) {
    const lowered = name.toLowerCase();
    const matchByName = plans.find(
      (plan) => plan.name.toLowerCase() === lowered && matchesBillingType(plan)
    );
    if (matchByName) {
      return matchByName;
    }
  }

  return null;
};

const buildFallbackPlan = (
  selection: StoredMembershipSelection | null
): IMembershipPlan | null => {
  if (!selection?.name) {
    return null;
  }

  const billingLabel =
    selection.billingPeriod ??
    (selection.billingType ? BILLING_LABELS[selection.billingType] : undefined);

  return {
    id: selection.id,
    planCode: selection.planCode ?? undefined,
    name: selection.name,
    description: "",
    price: selection.price ?? undefined,
    billingPeriod: billingLabel,
    benefits: [],
    logoColor: "#6e8968",
    billingType: selection.billingType,
    amount: selection.amount ?? undefined,
    currency: selection.currency ?? undefined,
  };
};

export default function MembershipConfirm() {
  const navigate = useNavigate();
  const [pendingPaymentUrl, setPendingPaymentUrl] = useState<string | null>(
    null
  );
  const [isCheckingPayment, setIsCheckingPayment] = useState(false);
  const [storedSelection] = useState<StoredMembershipSelection | null>(() => {
    try {
      const rawValue = getMembershipSelectionRaw();
      const parsed = parseStoredSelection(rawValue);

      if (!parsed) {
        clearMembershipSelection();
      }

      return parsed;
    } catch {
      clearMembershipSelection();
      return null;
    }
  });

  const { data, isLoading } = useMembershipLevels();
  const membershipStatusQuery = useMembershipStatus({
    retry: false,
    staleTime: 60_000,
    refetchInterval: pendingPaymentUrl
      ? PAYMENT_STATUS_REFETCH_INTERVAL_MS
      : false,
  });
  const profileQuery = useProfile({
    retry: 1,
    staleTime: 60_000,
    refetchInterval: pendingPaymentUrl
      ? PAYMENT_STATUS_REFETCH_INTERVAL_MS
      : false,
  });
  const { data: membershipStatus } = membershipStatusQuery;
  const { data: profile } = profileQuery;
  const { mutateAsync: setupMembership, isPending: isSubmitting } =
    useSetupMembership();
  const isKycVerified = isKycVerifiedStatus(profile?.kyc?.status ?? null);

  useEffect(() => {
    if (pendingPaymentUrl) {
      return;
    }

    if (!membershipStatus?.status) {
      return;
    }

    if (membershipStatus.status.toUpperCase() === "ACTIVE") {
      navigate(resolveRouteForActiveMembership({ isKycVerified }), {
        replace: true,
      });
    }
  }, [isKycVerified, membershipStatus?.status, navigate, pendingPaymentUrl]);

  useEffect(() => {
    if (!pendingPaymentUrl) {
      return;
    }

    const targetRoute = resolveRouteAfterPendingPaymentCheck({
      membershipStatus: membershipStatus?.status,
      kycStatus: profile?.kyc?.status,
      kycAttemptStatus: profile?.kyc?.attemptStatus,
    });

    if (targetRoute) {
      clearMembershipSelection();
      navigate(targetRoute, { replace: true });
    }
  }, [
    membershipStatus?.status,
    navigate,
    pendingPaymentUrl,
    profile?.kyc?.attemptStatus,
    profile?.kyc?.status,
  ]);

  const sortedLevels = useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }

    return sortMembershipLevels(data);
  }, [data]);

  const remotePlans = useMemo(() => {
    if (sortedLevels.length === 0) {
      return [];
    }

    const monthly = sortedLevels.map((level, index) =>
      mapMembershipLevelToPlan(level, "MONTHLY", index)
    );
    const annual = sortedLevels.map((level, index) =>
      mapMembershipLevelToPlan(level, "ANNUAL", index)
    );

    return [...monthly, ...annual];
  }, [sortedLevels]);

  const selectedPlan = useMemo(() => {
    if (!storedSelection) {
      return null;
    }

    const planFromRemote = findPlanMatch(remotePlans, storedSelection);
    if (planFromRemote) {
      return planFromRemote;
    }

    return buildFallbackPlan(storedSelection);
  }, [remotePlans, storedSelection]);

  const membershipPlanId = storedSelection?.id ?? selectedPlan?.id ?? undefined;
  const billingType =
    storedSelection?.billingType ?? selectedPlan?.billingType ?? undefined;

  const handleConfirm = async () => {
    if (!storedSelection) {
      toast.error("Please choose a membership plan first.");
      navigate("/plan/membership");
      return;
    }

    const planId = membershipPlanId;
    const billing = billingType;

    if (!planId || !billing) {
      toast.error(
        "We couldn't determine your membership selection. Please choose a plan again."
      );
      navigate("/plan/membership");
      return;
    }

    const checkoutWindow = openPendingPaymentCheckoutWindow();

    try {
      const response = await setupMembership({
        membershipPlanId: planId,
        billingType: billing,
        callbackUrl:
          typeof window !== "undefined"
            ? buildPaymentSuccessCallbackUrl(window.location.href)
            : undefined,
      });

      const setupStatus = response.status ?? response.membership?.status;

      if (setupStatus?.toUpperCase() === "PENDING" && response.paymentUrl) {
        toast.success("Payment page opened in a new tab.");
        setPendingPaymentUrl(response.paymentUrl);
        redirectToPaymentCheckout(response.paymentUrl, checkoutWindow);
        void membershipStatusQuery.refetch();
        void profileQuery.refetch();
        return;
      }

      if (checkoutWindow && !checkoutWindow.closed) {
        checkoutWindow.close();
      }

      const eduKycUrl = resolveEduKycUrlAfterMembershipSetup({
        kycUrl: response.kycUrl ?? profile?.kycUrl,
        eduKycUrl: response.eduKycUrl ?? profile?.eduKycUrl,
        isKycVerified,
      });

      if (eduKycUrl) {
        toast.success("Membership setup confirmed. Continue your KYC.");
        clearMembershipSelection();
        window.location.assign(eduKycUrl);
        return;
      }

      toast.success("Membership setup confirmed.");
      clearMembershipSelection();
      navigate(
        resolveRouteAfterSetupMembership({
          forceVerifyAfterPayment: setupStatus?.toUpperCase() === "PENDING",
          isKycVerified,
        }),
        { replace: true }
      );
    } catch (error) {
      if (checkoutWindow && !checkoutWindow.closed) {
        checkoutWindow.close();
      }

      if (isApiError(error)) {
        const message =
          error.message ?? "Unable to set up your membership right now.";
        toast.error(message);

        if (
          message.toLowerCase().includes("membership setup already completed")
        ) {
          clearMembershipSelection();
          navigate(resolveRouteAfterSetupMembershipAlreadyCompleted(), {
            replace: true,
          });
          return;
        }

        if (error.errorName === "membership-not-found") {
          navigate("/plan/membership");
        }
        return;
      }

      toast.error("Unable to set up your membership right now.");
    }
  };

  const handleCheckPayment = async () => {
    setIsCheckingPayment(true);

    try {
      const [latestMembership, latestProfile] = await Promise.all([
        membershipStatusQuery.refetch(),
        profileQuery.refetch(),
      ]);
      const targetRoute = resolveRouteAfterPendingPaymentCheck({
        membershipStatus: latestMembership.data?.status,
        kycStatus: latestProfile.data?.kyc?.status,
        kycAttemptStatus: latestProfile.data?.kyc?.attemptStatus,
      });

      if (targetRoute) {
        clearMembershipSelection();
        navigate(targetRoute, { replace: true });
        return;
      }

      toast.info("Payment is still pending. We'll keep checking.");
    } finally {
      setIsCheckingPayment(false);
    }
  };

  const handleChangePendingPlan = () => {
    clearMembershipSelection();
    navigate("/plan/membership");
  };

  const handleOpenPaymentLink = () => {
    if (!pendingPaymentUrl) {
      return;
    }

    const paymentWindow = openPaymentCheckoutLink(pendingPaymentUrl);

    if (!paymentWindow) {
      window.location.assign(pendingPaymentUrl);
    }
  };

  const renderPlanDetails = () => {
    if (!storedSelection) {
      return (
        <div className="space-y-2 text-white">
          <p className="opacity-80">No membership selected yet.</p>
          <p className="opacity-60 text-sm">
            Go back and choose a plan to continue.
          </p>
        </div>
      );
    }

    if (!selectedPlan && isLoading) {
      return (
        <div className="flex justify-center py-4">
          <Spinner className="size-6 text-white" />
        </div>
      );
    }

    if (!selectedPlan) {
      return (
        <div className="space-y-2 text-white">
          <p className="opacity-80">
            We couldn't load your membership details.
          </p>
          <p className="opacity-60 text-sm">
            Please pick a plan again to continue.
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-2 text-white">
        <p className="opacity-80">
          You selected{" "}
          <span className="font-semibold">{selectedPlan.name}</span>.
        </p>
        {selectedPlan.price && selectedPlan.billingPeriod ? (
          <p className="opacity-70">
            {selectedPlan.price} • {selectedPlan.billingPeriod}
          </p>
        ) : (
          <p className="opacity-70">No subscription fee applies</p>
        )}
        <p className="opacity-50 text-sm">
          Review your choice and confirm to activate your membership.
        </p>
      </div>
    );
  };

  return (
    <div className="w-[350px]">
      <LinearLayout orientation="vertical">
        <div className="text-center align-middle mx-auto space-y-6">
          <h1 className="mx-auto w-[272px] text-center text-white font-medium text-[20px] leading-[24px] font-inter">
            {pendingPaymentUrl
              ? "Complete Your Payment"
              : "Confirm Your Membership"}
          </h1>

          {pendingPaymentUrl ? (
            <div className="space-y-4 text-white">
              <div className="flex justify-center">
                <Spinner className="size-8 text-white" />
              </div>
              <div className="space-y-2">
                <p className="opacity-80">
                  We opened the payment page in a new tab.
                </p>
                <p className="text-sm opacity-60">
                  Keep this page open. We'll refresh your membership status
                  every 30 seconds.
                </p>
              </div>
              {membershipStatusQuery.isFetching || profileQuery.isFetching ? (
                <p className="text-xs text-white/40">
                  Checking latest membership status...
                </p>
              ) : null}
            </div>
          ) : (
            renderPlanDetails()
          )}

          <div className="flex flex-col gap-4">
            {pendingPaymentUrl ? (
              <>
                <Button
                  variant="primary"
                  fluid
                  loading={isCheckingPayment}
                  onClick={handleCheckPayment}>
                  Check payment
                </Button>
                <Button
                  variant="secondary"
                  fluid
                  onClick={handleOpenPaymentLink}>
                  Open payment link
                </Button>
                <Button
                  variant="outlined"
                  fluid
                  onClick={handleChangePendingPlan}>
                  Cancel and change plan
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="secondary"
                  fluid
                  onClick={() => navigate("/plan/membership")}>
                  Change plan
                </Button>
                <Button
                  variant="primary"
                  fluid
                  loading={isSubmitting}
                  disabled={!membershipPlanId || !billingType}
                  onClick={handleConfirm}>
                  Confirm and Continue
                </Button>
              </>
            )}
          </div>
        </div>
      </LinearLayout>
    </div>
  );
}
